import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { Close, FileCopy, Warning } from "@material-ui/icons";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Skeleton } from "@material-ui/lab";
import React from "react";
import { IdentificationStatus, FeeStatus, RegistrationStatus } from "../../Base/AdoptionConstants";
import {
  activateRegistration,
  inactivateRegistration,
  loadRegistrationFromFriendlyId,
} from "../../Base/Services/RegistrationService";
import RegistrationAdoptionDocuments from "./RegistrationAdoptionDocuments";
import RegistrationIdentityVerification from "./RegistrationIdentityVerification";
import RegistrationPaymentVerification from "./RegistrationPaymentVerification";
import RegistrationFields from "../RegistrationFieldsComponents/RegistrationFields";
import AdoptionDetails from "./AdoptionDetails";
import useAdminContext from "../AdminContextProvider";
import ButtonWithLoadingSpinner from "../../Base/Components/ButtonWithLoadingSpinner";
import useAlerts from "../../Base/AlertProvider";
import RegistrationChangeLogSection from "./RegistrationChangeLogSection";
import MatchesForRegistrationSection from "./MatchesForRegistrationSection";

const useStyles = makeStyles((theme) => ({
  errorColor: {
    color: theme.palette.error.dark,
    borderColor: theme.palette.error.dark,
  },
  paperRegistration: {
    color: theme.palette.secondary.main,
  },
  legacyRegistration: {
    color: theme.palette.secondary.main,
  },
  responsiveDialog: {
    [theme.breakpoints.down("xs")]: {
      margin: 0,
      maxWidth: "100%",
      width: "100%",
      height: "100%",
      maxHeight: "none",
      borderRadius: 0,
    },
  },
  compactTitle: {
    [theme.breakpoints.down("sm")]: {
      padding: "0.25rem 0.5rem",
    },
  },
  compactActions: {
    [theme.breakpoints.down("sm")]: {
      padding: "0.25rem 0.5rem",
    },
  },
  compactContent: {
    [theme.breakpoints.down("sm")]: {
      padding: "0.25rem 0.5rem",
    },
  },
  form: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
}));

export default function RegistrationDialog({ open, onClose, userFriendlyRegistrationId, sectionToOpen }) {
  const classes = useStyles();
  const [processing, setProcessing] = React.useState(false);
  const [registration, setRegistration] = React.useState();
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [paymentOpen, setPaymentOpen] = React.useState(false);
  const [identityOpen, setIdentityOpen] = React.useState(false);
  const [adoptionDetailsOpen, setAdoptionDetailsOpen] = React.useState(false);
  const [adoptionDocumentsOpen, setAdoptionDocumentsOpen] = React.useState(false);
  const [changesOpen, setChangesOpen] = React.useState(false);
  const [matchesOpen, setMatchesOpen] = React.useState(false);
  const adminContext = useAdminContext();
  const { addErrorAlert } = useAlerts();

  React.useEffect(() => {
    if (!open) {
      setDetailsOpen(false);
      setPaymentOpen(false);
      setIdentityOpen(false);
      setAdoptionDetailsOpen(false);
      setAdoptionDocumentsOpen(false);
      setMatchesOpen(false);
      setChangesOpen(false);
      setRegistration(null);
    }
  }, [open]);

  React.useEffect(() => {
    if (sectionToOpen === "details") {
      setDetailsOpen(true);
    }
    if (sectionToOpen === "payment") {
      setPaymentOpen(true);
    }
    if (sectionToOpen === "identity") {
      setIdentityOpen(true);
    }
    if (sectionToOpen === "adoptionDocuments") {
      setAdoptionDocumentsOpen(true);
    }
    if (sectionToOpen === "changes") {
      setChangesOpen(true);
    }
    if (sectionToOpen === "matches") {
      setMatchesOpen(true);
    }
  }, [sectionToOpen]);

  React.useEffect(() => {
    if (userFriendlyRegistrationId) {
      loadRegistrationFromFriendlyId(userFriendlyRegistrationId).then(setRegistration);
    }
  }, [userFriendlyRegistrationId]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth={true}
        maxWidth="md"
        aria-labelledby="registration-dialog-title"
        aria-describedby="registration-dialog-content"
        classes={{ paper: classes.responsiveDialog }}
      >
        <DialogTitle id="registration-dialog-title" className={classes.compactTitle}>
          <span style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Registration {userFriendlyRegistrationId}</span>
            {registration?.active === false && (
              <span className={classes.errorColor} style={{ display: "flex", alignItems: "center" }}>
                <Warning style={{ marginRight: 8 }} /> INACTIVE REGISTRATION
              </span>
            )}
            {registration?.paperRegistration && (
              <span className={classes.paperRegistration} style={{ display: "flex", alignItems: "center" }}>
                <FileCopy style={{ marginRight: 8 }} /> Paper Registration
              </span>
            )}
            {registration?.legacyRegistration && (
              <span className={classes.legacyRegistration} style={{ display: "flex", alignItems: "center" }}>
                <FileCopy style={{ marginRight: 8 }} /> Legacy Registration
              </span>
            )}
            <IconButton size="small" color="default" onClick={onClose} aria-label="Close Dialog">
              <Close />
            </IconButton>
          </span>
        </DialogTitle>
        <DialogContent id="registration-dialog-content" className={classes.compactContent}>
          {registration ? (
            <>
              <Accordion expanded={detailsOpen} style={{ width: "100%" }}>
                <AccordionSummary
                  arial-label="Expand Details"
                  aria-controls="details-selection"
                  id="details-selection"
                  onClick={() => setDetailsOpen(!detailsOpen)}
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Typography variant="body1" style={{ margin: 0, fontWeight: 600 }}>
                    Registration Details
                  </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ flexDirection: "column" }}>
                  <RegistrationFields registration={registration} />
                </AccordionDetails>
              </Accordion>
              <Accordion expanded={identityOpen} style={{ width: "100%" }}>
                <AccordionSummary
                  arial-label="Expand Identity Verification"
                  aria-controls="identity-selection"
                  id="identity-selection"
                  onClick={() => setIdentityOpen(!identityOpen)}
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Typography variant="body1" style={{ margin: 0, fontWeight: 600 }}>
                    Identity Verification
                  </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ flexDirection: "column" }}>
                  <RegistrationIdentityVerification setRegistration={setRegistration} registration={registration} />
                </AccordionDetails>
              </Accordion>
              <Accordion expanded={paymentOpen} style={{ width: "100%" }}>
                <AccordionSummary
                  arial-label="Expand Payment Verification"
                  aria-controls="payment-selection"
                  id="payment-selection"
                  onClick={() => setPaymentOpen(!paymentOpen)}
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Typography variant="body1" style={{ margin: 0, fontWeight: 600 }}>
                    Payment Information
                  </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ flexDirection: "column" }}>
                  <RegistrationPaymentVerification setRegistration={setRegistration} registration={registration} />
                </AccordionDetails>
              </Accordion>
              <Accordion expanded={adoptionDetailsOpen} style={{ width: "100%" }}>
                <AccordionSummary
                  arial-label="Expand Adoption Details"
                  aria-controls="adoption-details-selection"
                  id="adoption-details-selection"
                  onClick={() => setAdoptionDetailsOpen(!adoptionDetailsOpen)}
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Typography variant="body1" style={{ margin: 0, fontWeight: 600 }}>
                    Birth Details
                  </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ flexDirection: "column" }}>
                  <AdoptionDetails registration={registration} />
                </AccordionDetails>
              </Accordion>
              {registration.identificationStatus === IdentificationStatus.VERIFIED.value &&
                (registration.feeStatus === FeeStatus.FEE_PAID.value ||
                  registration.feeStatus === FeeStatus.FEE_WAIVED.value ||
                  registration.feeStatus === FeeStatus.UPDATE_FEE_PENDING.value) && (
                  <Accordion expanded={adoptionDocumentsOpen} style={{ width: "100%" }}>
                    <AccordionSummary
                      arial-label="Expand Adoption Documents"
                      aria-controls="adoption-documents-selection"
                      id="adoption-documents-selection"
                      onClick={() => setAdoptionDocumentsOpen(!adoptionDocumentsOpen)}
                      expandIcon={<ExpandMoreIcon />}
                    >
                      <Typography variant="body1" style={{ margin: 0, fontWeight: 600 }}>
                        Adoption Documents
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails style={{ flexDirection: "column" }}>
                      <RegistrationAdoptionDocuments registration={registration} />
                    </AccordionDetails>
                  </Accordion>
                )}

              <Accordion expanded={matchesOpen} style={{ width: "100%" }}>
                <AccordionSummary
                  arial-label="Expand Matches"
                  aria-controls="matches-selection"
                  id="matches-selection"
                  onClick={() => setMatchesOpen(!matchesOpen)}
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Typography variant="body1" style={{ margin: 0, fontWeight: 600 }}>
                    Matches
                  </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ flexDirection: "column" }}>
                  <MatchesForRegistrationSection registration={registration} />
                </AccordionDetails>
              </Accordion>
              <Accordion expanded={changesOpen} style={{ width: "100%" }}>
                <AccordionSummary
                  arial-label="Expand Changes"
                  aria-controls="changes-selection"
                  id="changes-selection"
                  onClick={() => setChangesOpen(!changesOpen)}
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Typography variant="body1" style={{ margin: 0, fontWeight: 600 }}>
                    Registration Change Log
                  </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ flexDirection: "column" }}>
                  <RegistrationChangeLogSection registration={registration} />
                </AccordionDetails>
              </Accordion>
            </>
          ) : (
            <div>
              <Skeleton style={{ height: 40 }} />
              <Skeleton style={{ height: 40 }} />
              <Skeleton style={{ height: 40 }} />
            </div>
          )}
        </DialogContent>
        <DialogActions className={classes.compactActions} style={{ justifyContent: "space-between" }}>
          <div>
            {registration?.active === true && registration?.registrationStatus !== RegistrationStatus.MATCHED.value && (
              <ButtonWithLoadingSpinner
                isLoading={processing}
                className={classes.errorColor}
                variant="outlined"
                onClick={() => {
                  if (window.confirm("Are you sure you want to inactivate this record?")) {
                    setProcessing(true);
                    inactivateRegistration(registration.registrationId)
                      .then(setRegistration)
                      .then(adminContext.triggerDataResync)
                      .catch(() => addErrorAlert("There was an error completing the operation"))
                      .finally(() => setProcessing(false));
                  }
                }}
              >
                Inactivate
              </ButtonWithLoadingSpinner>
            )}
            {registration?.active === false && (
              <ButtonWithLoadingSpinner
                isLoading={processing}
                color="secondary"
                variant="outlined"
                onClick={() => {
                  setProcessing(true);
                  activateRegistration(registration.registrationId)
                    .then(setRegistration)
                    .then(adminContext.triggerDataResync)
                    .catch(() => addErrorAlert("There was an error completing the operation"))
                    .finally(() => setProcessing(false));
                }}
              >
                Reactivate
              </ButtonWithLoadingSpinner>
            )}
          </div>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
